import { Link } from 'react-router';
import LegalPageLayout from '../components/legal/LegalPageLayout';
import { LegalEmail, Term, Value } from '../components/legal/LegalPrimitives';
import { legalConfig, legalDates } from '../config/legal';

/**
 * Each entry is one numbered section. The contents list and the body are both
 * built from this array, so a section cannot appear in one and not the other.
 *
 * DISCLOSURE LEVEL: this document says what information is collected, why, who
 * it goes to and how long it is kept. It deliberately does NOT describe how any
 * of that is implemented: no pipeline stages, no derivative generation, no
 * access-control mechanics, no storage topology, no identifier design, no
 * retention job schedules. If an edit here would help a reader rebuild the
 * product, or attack it, it does not belong on a public page.
 *
 * Product gaps these documents must not paper over are tracked internally in
 * docs/LEGAL_PRELAUNCH_GAPS.md, never on this page.
 */
const sections = [
  {
    id: 'about-this-policy',
    title: 'About this policy',
    content: (
      <>
        <p>
          ShareShutter lets someone hosting an event create a shared collection, invite
          guests with a QR code or a link, and gather the photos and videos everyone took
          in one place. This policy explains what information we collect, why we collect
          it, who it is shared with, how long we keep it, and what choices you have.
        </p>
        <p>
          It applies to the ShareShutter mobile applications, the ShareShutter website,
          the ShareShutter web console, and the services behind them, which we refer to
          together as the <Term>Services</Term>. In this policy{' '}
          <Term>ShareShutter</Term>, <Term>we</Term>, <Term>us</Term> and <Term>our</Term>{' '}
          mean <Value>{legalConfig.legalEntity}</Value>.
        </p>
        <p>
          Your use of the Services is also governed by our{' '}
          <Link to="/terms">Terms of Use</Link>.
        </p>
      </>
    ),
  },
  {
    id: 'account-information',
    title: 'Information you give us when you create an account',
    content: (
      <>
        <p>When you create a ShareShutter account we collect:</p>
        <ul>
          <li>
            <strong>A username.</strong> This is the name other people in a collection see
            beside the media you upload and in the member list, so choose accordingly.
          </li>
          <li>
            <strong>An email address.</strong> Used to sign you in and to reach you about
            your account.
          </li>
          <li>
            <strong>A profile photo, if you add one.</strong> This is optional. It is
            shown to other people in collections you join.
          </li>
          <li>
            <strong>An account identifier and the date the account was created.</strong>
          </li>
        </ul>
        <p>
          <strong>We do not store your password.</strong> Sign-in is handled by our
          authentication provider, Google Firebase Authentication. Your password is set
          and verified there rather than held by us.
        </p>
        <p>
          ShareShutter does not ask for your phone number, your date of birth, your
          address, or any payment details when you create an account.
        </p>
      </>
    ),
  },
  {
    id: 'collection-information',
    title: 'Collections you create or join',
    content: (
      <>
        <p>
          When you create a collection as a host, we store what you enter and what the
          collection needs in order to work:
        </p>
        <ul>
          <li>The collection name and description.</li>
          <li>A cover image, if you choose one.</li>
          <li>The event date, if you set one.</li>
          <li>
            The plan chosen for the collection, its storage allowance and how much has
            been used, how many items it holds, and when it expires.
          </li>
          <li>The date the collection was created, and whether it is still open.</li>
        </ul>
        <p>
          When you join a collection as a guest, we record your membership of it: which
          collection, your account, your role, and the date you joined. Your username and
          profile photo are shown to the other people in that collection.
        </p>
      </>
    ),
  },
  {
    id: 'user-content',
    title: 'Photos, videos and other content you upload',
    content: (
      <>
        <p>
          Hosts and guests can both upload photos and videos to a collection. Together
          with collection covers, descriptions and profile photos, we call this{' '}
          <Term>User Content</Term>. User Content is the heart of the Services and it is
          the most significant category of information we hold.
        </p>
        <p>
          User Content you add to a collection is available to the other people
          authorised to be in that collection. A collection is not a private space
          between you and ShareShutter: it is a shared one, and everyone in it can see
          what has been added and who added it.
        </p>
        <p>
          Only upload photos and videos you have the right to share, and be considerate
          about images of other people.{' '}
          <a href="#collection-visibility">Who can see a collection</a> explains who can
          reach a collection, and our <Link to="/terms">Terms of Use</Link> set out what
          may and may not be uploaded.
        </p>
      </>
    ),
  },
  {
    id: 'media-metadata',
    title: 'Information about the files you upload',
    content: (
      <>
        <p>
          Alongside each photo or video, we hold basic information about the file itself
          so that it can be stored, shown to you, and counted against the collection's
          allowance:
        </p>
        <ul>
          <li>The original file name and the type of file.</li>
          <li>Its size, its dimensions, and for a video its length.</li>
          <li>When it was uploaded, and which account uploaded it.</li>
        </ul>
        <p>
          <strong>About camera metadata.</strong> Photos and videos from phones and
          cameras often carry information embedded in the file itself, such as the capture
          time, the camera model and, in some cases, the location where it was taken.
          ShareShutter does not read, index, search or use that embedded information.
        </p>
        <p>
          Your original file is kept as you uploaded it, so anything your camera embedded
          in it stays inside that file, and someone who can view or download the original
          from the collection may be able to read it. If that matters to you, remove the
          metadata before uploading.
        </p>
      </>
    ),
  },
  {
    id: 'device-permissions',
    title: 'Camera and photo library permissions',
    content: (
      <>
        <p>
          The mobile applications ask your device for access to your camera and your
          photo library, and only for specific features:
        </p>
        <ul>
          <li>
            <strong>Camera.</strong> To scan a collection QR code, and to take a photo or
            video to add to a collection.
          </li>
          <li>
            <strong>Photo library.</strong> To let you pick the specific photos and
            videos you want to upload, and to choose a profile photo or a collection
            cover.
          </li>
        </ul>
        <p>
          <strong>ShareShutter does not scan, index or upload your camera roll.</strong>{' '}
          Only the items you select are read, and only those items are uploaded. When you
          scan a QR code, the camera is used to read the code on your device, and we do
          not receive or keep what the camera sees.
        </p>
        <p>
          You control these permissions in your device settings and can withdraw them at
          any time, which will stop the features that depend on them from working.
        </p>
      </>
    ),
  },
  {
    id: 'automatic-information',
    title: 'Information collected automatically',
    content: (
      <>
        <p>
          <strong>In the mobile applications.</strong> The mobile apps use Google
          Analytics for Firebase, which records which screens are opened along with the
          identifiers and device and app information that service collects. Released
          builds also use Firebase Crashlytics, which reports crashes and errors so that
          we can fix them.
        </p>
        <p>
          <strong>In the web console and on this website.</strong> Neither uses analytics
          or crash reporting. This website sets no cookies and stores nothing in your
          browser. See <a href="#cookies">Cookies and browser storage</a>.
        </p>
        <p>
          <strong>On our servers.</strong> As with any internet service, our systems
          record operational information about the requests they handle, such as IP
          address, timestamps and errors, and we use monitoring tools to detect and
          diagnose faults.
        </p>
        <p>
          ShareShutter does not use advertising identifiers, does not run advertising, and
          does not build advertising or marketing profiles about you.
        </p>
      </>
    ),
  },
  {
    id: 'invitations',
    title: 'Invitation links and QR codes',
    content: (
      <>
        <p>
          A collection invitation is a link to a particular collection, and a QR code is
          that same link in a form a camera can read.
        </p>
        <p>When you follow an invitation and join a collection, we process:</p>
        <ul>
          <li>Which collection the invitation was for.</li>
          <li>Your account identity, once you are signed in.</li>
          <li>The date and time you joined, and your role in the collection.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'issue-reports',
    title: 'Reporting an issue',
    content: (
      <>
        <p>
          The account menu has a <strong>Report an issue</strong> form. When you send one,
          we store the description you write, the category you pick, your account
          identifier, your username and email address, the platform and app version you
          are using, and the time you sent it.
        </p>
        <p>
          Issue reports go to a support queue that other users cannot read. We use them to
          answer you and to fix what you reported.
        </p>
      </>
    ),
  },
  {
    id: 'how-we-use',
    title: 'How we use information',
    content: (
      <>
        <p>We use the information described above to:</p>
        <ul>
          <li>Create and maintain your account, and sign you in.</li>
          <li>Create collections, and manage who is a member of them.</li>
          <li>Receive, store and show the photos and videos you upload.</li>
          <li>Apply storage, file size and format limits.</li>
          <li>Let people join a collection from an invitation.</li>
          <li>Answer support requests and issue reports.</li>
          <li>
            Detect, investigate and prevent abuse, fraud and security incidents, and
            enforce our Terms of Use.
          </li>
          <li>
            Diagnose faults, and improve the reliability and performance of the Services.
          </li>
          <li>Comply with legal obligations and respond to lawful requests.</li>
        </ul>
        <p>
          We do not use your photos, videos or account information for advertising, and we
          do not use your User Content to train machine learning models.
        </p>
      </>
    ),
  },
  {
    id: 'collection-visibility',
    title: 'Who can see a collection',
    content: (
      <>
        <p>
          A collection is private to its members. Someone has to be a member of a
          collection to see the photos and videos in it or to add to it.
        </p>
        <p>
          <strong>
            An invitation should not be treated as a secret once you have shared it.
          </strong>{' '}
          Anyone you send it to can pass it on, and anyone who receives it can ask to
          join. Share invitations with the people you actually want in the collection, and
          remember that a printed QR code can be used by anyone who walks past it.
        </p>
        <p>
          Within a collection, other members can see the photos and videos you upload, the
          username and profile photo shown against them, and your entry in the member
          list. Your email address is not shown to other members.
        </p>
        <p>
          A host can close or delete a collection. Guests cannot remove other people or
          delete another person's media.
        </p>
      </>
    ),
  },
  {
    id: 'storage-and-processing',
    title: 'Where your information is held',
    content: (
      <>
        <p>
          ShareShutter runs on cloud infrastructure from established third-party
          providers. Your photos and videos, and the account and collection information
          that goes with them, are held using those providers on our behalf and under
          contract with us. They are listed in{' '}
          <a href="#service-providers">Service providers</a>.
        </p>
        <p>
          We take steps to control who can reach that information. We do not publish the
          details of how, because doing so would make the Services easier to attack.{' '}
          <a href="#security">Security</a> describes our approach in general terms.
        </p>
      </>
    ),
  },
  {
    id: 'service-providers',
    title: 'Service providers',
    content: (
      <>
        <p>
          We use a small number of providers to operate ShareShutter. They process
          information on our behalf, under contract, and only for the purposes we set:
        </p>
        <ul>
          <li>
            <strong>Google (Firebase).</strong> Account sign-in, application services, and
            analytics and crash reporting in the mobile applications.
          </li>
          <li>
            <strong>Microsoft (Azure).</strong> Cloud hosting, storage and supporting
            infrastructure for the Services.
          </li>
          <li>
            <strong>Website and application hosting providers</strong>, which serve this
            website and the web console.
          </li>
        </ul>
        <p>
          These providers may handle information only to provide their service to us. We
          do not permit them to use it for their own purposes.
        </p>
      </>
    ),
  },
  {
    id: 'payments',
    title: 'Payments',
    content: (
      <>
        <p>
          <strong>
            ShareShutter does not currently collect or process any payment information.
          </strong>{' '}
          We hold no card details, no billing addresses and no transaction records.
        </p>
        <p>
          If paid plans are introduced, payments will be handled by a third-party payment
          processor, or by Apple or Google where a purchase is made through their stores.
          Card details would go to that processor rather than to us, and we would expect
          to receive only transaction information such as the plan bought, the amount, the
          payment status and a transaction reference. We will update this policy before
          that happens.
        </p>
      </>
    ),
  },
  {
    id: 'sharing',
    title: 'When we share information',
    content: (
      <>
        <p>We share information in these situations and no others:</p>
        <ul>
          <li>
            <strong>With other people in a collection.</strong> As described in{' '}
            <a href="#collection-visibility">Who can see a collection</a>.
          </li>
          <li>
            <strong>With service providers.</strong> As described in{' '}
            <a href="#service-providers">Service providers</a>.
          </li>
          <li>
            <strong>For legal reasons.</strong> Where we reasonably believe it is
            necessary to comply with a law or valid legal process, to enforce our Terms of
            Use, or to protect the rights, property or safety of our users, the public or
            ShareShutter, including investigating fraud, abuse and security issues.
          </li>
          <li>
            <strong>In a business transaction.</strong> If ShareShutter is involved in a
            merger, acquisition, financing, reorganisation or sale of assets, information
            may transfer as part of that transaction, subject to applicable law. We will
            give notice where required.
          </li>
          <li>
            <strong>With your direction.</strong> Where you ask us to share something, or
            otherwise consent to it.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'no-selling',
    title: 'We do not sell your information',
    content: (
      <>
        <p>
          ShareShutter does not sell your personal information, and does not share it for
          cross-context behavioural advertising or targeted advertising. We do not run
          advertising in the Services.
        </p>
        <p>
          This describes what we do today. If that ever changes we will update this policy
          and provide any notice or choice the law requires before the change takes
          effect.
        </p>
      </>
    ),
  },
  {
    id: 'retention',
    title: 'How long we keep information',
    content: (
      <>
        <p>How long we hold something depends on what it is:</p>
        <ul>
          <li>
            <strong>Account information</strong> is kept while your account exists.
          </li>
          <li>
            <strong>Collections and their media</strong> are kept for the period that goes
            with the collection's plan. Collections have an expiry date, and media in a
            collection may become unavailable and be removed after it. The allowance and
            duration for each plan are shown in the app when a collection is created.
          </li>
          <li>
            <strong>A collection a host deletes</strong> becomes unavailable immediately:
            it disappears from every list, its link stops working, and nobody can upload to
            it. It is then deleted from our systems after a limited period.
          </li>
          <li>
            <strong>Issue reports</strong> are kept while they are needed for support and
            for our records.
          </li>
          <li>
            <strong>Operational logs</strong> are kept for a limited period for security
            and troubleshooting.
          </li>
        </ul>
        <p>
          We may keep information for longer where we need to in order to comply with a
          legal obligation, resolve a dispute or enforce our agreements. Backups may hold
          copies for a period after deletion, and those copies are overwritten in the
          ordinary course.
        </p>
      </>
    ),
  },
  {
    id: 'your-controls',
    title: 'Deleting your information',
    content: (
      <>
        <p>In the app you can:</p>
        <ul>
          <li>
            <strong>Edit your profile</strong> to change your username or profile photo.
          </li>
          <li>
            <strong>Delete a collection you host</strong>, which removes access to it and
            to the media in it, as described in{' '}
            <a href="#retention">How long we keep information</a>.
          </li>
        </ul>
        <p>
          For anything else, including closing your account, removing an individual photo
          or video, leaving a collection, or asking for a copy of your information, email
          us at <LegalEmail address={legalConfig.privacyEmail} /> from the address on your
          account and we will deal with your request.
        </p>
      </>
    ),
  },
  {
    id: 'backups',
    title: 'Keeping your own copies',
    content: (
      <>
        <p>This section matters more than most, so we would rather be direct about it.</p>
        <p>
          <strong>
            ShareShutter is a way to gather and share the photos and videos from an event.
            It is not a permanent archive, and it should not be the only place an
            irreplaceable photo or video exists.
          </strong>
        </p>
        <p>
          Collections expire. Hosts can delete collections. Accounts can be lost. Software
          and hardware fail, providers have outages, files can be corrupted, and uploads
          can fail in ways that are not always recoverable. We take reasonable care to run
          the Services reliably and to look after what you entrust to us, but we cannot
          guarantee that content will always be available or recoverable.
        </p>
        <p>
          Please keep your own copies of anything that matters to you, and download what
          you want to keep from a collection before it expires.
        </p>
      </>
    ),
  },
  {
    id: 'security',
    title: 'Security',
    content: (
      <>
        <p>
          We use commercially reasonable administrative, technical and organisational
          measures designed to protect information against loss and against unauthorised
          access, use or disclosure, and we review them as the Services develop.
        </p>
        <p>
          No internet service or storage system can be completely secure, and we cannot
          guarantee the security of information transmitted to or stored by us. Please
          protect your account: use a strong, unique password, keep it to yourself, and
          tell us at <LegalEmail address={legalConfig.supportEmail} /> if you think someone
          else has access to your account.
        </p>
        <p>
          We do not publish the details of our security measures, because doing so would
          make the Services easier to attack.
        </p>
      </>
    ),
  },
  {
    id: 'international',
    title: 'International processing',
    content: (
      <>
        <p>
          ShareShutter is operated using cloud infrastructure, and our providers may
          process and store information in data centres in more than one country,
          including the United States. Those countries may have different data protection
          laws from the country you live in.
        </p>
        <p>
          Where the law requires it, we rely on an appropriate transfer mechanism and on
          the contractual protections our providers offer. If you would like details of
          the regions your information is processed in, contact us at{' '}
          <LegalEmail address={legalConfig.privacyEmail} />.
        </p>
      </>
    ),
  },
  {
    id: 'children',
    title: "Children's privacy",
    content: (
      <>
        <p>
          The Services are not directed to children, and we do not knowingly collect
          personal information from anyone under the applicable minimum age of{' '}
          <Value>{legalConfig.minimumAge}</Value>. You must meet that minimum age to
          create a ShareShutter account.
        </p>
        <p>
          If you believe a child has given us personal information, contact us at{' '}
          <LegalEmail address={legalConfig.privacyEmail} /> and we will take reasonable
          steps to delete it.
        </p>
      </>
    ),
  },
  {
    id: 'your-rights',
    title: 'Your privacy rights',
    content: (
      <>
        <p>
          Depending on where you live, applicable law may give you the right to ask us to:
        </p>
        <ul>
          <li>
            Confirm what personal information we hold about you, and give you a copy.
          </li>
          <li>Correct information that is inaccurate or incomplete.</li>
          <li>Delete your personal information.</li>
          <li>Provide your information in a portable form.</li>
          <li>Restrict or object to certain processing.</li>
          <li>Withdraw consent where our processing relies on consent.</li>
          <li>Appeal a decision we make about a request.</li>
        </ul>
        <p>
          Not every right is available to everyone, and some are subject to exceptions. To
          make a request, email <LegalEmail address={legalConfig.privacyEmail} /> from the
          address on your account, or tell us how we can verify the request. We will
          respond within the time the applicable law allows, and we will not treat you
          differently for exercising a right.
        </p>
        <p>
          One limitation worth stating: we can act on your account and your own uploads,
          but a photo you appear in that somebody else uploaded belongs to their account.
          Tell us about it and we will handle it under the applicable law and our{' '}
          <Link to="/terms">Terms of Use</Link>.
        </p>
      </>
    ),
  },
  {
    id: 'us-states',
    title: 'United States state privacy rights',
    content: (
      <>
        <p>
          Several US states give residents rights over their personal information,
          including rights to know, access, correct, delete and obtain a copy, and to
          appeal a refusal. Where a state law applies to us and to you, we honour those
          rights through the process in <a href="#your-rights">Your privacy rights</a>.
        </p>
        <p>
          As stated in <a href="#no-selling">We do not sell your information</a>, we do not
          sell personal information and do not share it for cross-context behavioural
          advertising, so there is nothing for you to opt out of.
        </p>
      </>
    ),
  },
  {
    id: 'cookies',
    title: 'Cookies and browser storage',
    content: (
      <>
        <p>
          <strong>This website sets no cookies</strong> and stores nothing in your browser.
          There are no analytics cookies, no advertising cookies, and no tracking pixels on
          it.
        </p>
        <p>
          The <strong>web console</strong> at console.shareshutter.com is a signed-in
          application and stores information in your browser that is strictly necessary to
          keep you signed in between visits. It is not used to track you across other
          websites. Clearing your browser storage signs you out.
        </p>
        <p>
          If we ever add non-essential cookies we will update this policy and ask for
          consent where the law requires it.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    content: (
      <p>
        This policy will change as ShareShutter develops. When we change it we will update
        the Last Updated date at the top of this page. If a change is material we will
        provide additional notice where applicable law requires it.
      </p>
    ),
  },
  {
    id: 'contact',
    title: 'Contact us',
    content: (
      <>
        <p>For questions about this policy or about your information, contact:</p>
        <address className="legal__address">
          <span>ShareShutter</span>
          <span>
            <Value>{legalConfig.legalEntity}</Value>
          </span>
          <span>
            <Value>{legalConfig.mailingAddress}</Value>
          </span>
          <span>
            Privacy: <LegalEmail address={legalConfig.privacyEmail} />
          </span>
          <span>
            Support: <LegalEmail address={legalConfig.supportEmail} />
          </span>
        </address>
      </>
    ),
  },
];

export default function PrivacyPolicyPage() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      lastUpdated={legalDates.privacy}
      meta={{
        title: 'ShareShutter Privacy Policy',
        description:
          'Learn how ShareShutter collects, uses, stores, and protects information when you use our photo and video sharing services.',
        canonicalPath: '/privacy',
      }}
      intro={
        <p>
          This policy describes how ShareShutter handles your information: what we collect,
          why we collect it, who it is shared with, and what you can ask us to do with it.
        </p>
      }
      sections={sections}
    />
  );
}
